import { GridAggregationModel } from "@mui/x-data-grid-premium";
import { GRID_AGGREGATION_FUNCTIONS_NO_LABEL } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { useMemo } from "react";
import {
  HeatSeries,
  StrategyDashboardHeatData,
  UnitType,
} from "src/store/api/generatedApi";
import { outputMassSummaryName, weightedYieldPercent } from "./heatsRowFilter";

export const useHeatsTableAggregation = ({
  data,
  filteredSeries,
  isCostColumnsCollapsed,
  selectedSummaryName = null,
  isSummaryCollapsed = false,
}: {
  data: StrategyDashboardHeatData | undefined;
  filteredSeries: HeatSeries[];
  isCostColumnsCollapsed: boolean;
  selectedSummaryName?: string | null;
  isSummaryCollapsed?: boolean;
}) => {
  const aggregationModel = useMemo<GridAggregationModel>(() => {
    if (!data) return {};

    const baseEntries: [string, string][] = [
      ["heat_id", "size"],
      ["_materialsDiff", "sum"],
    ];

    const summaryEntries: [string, string][] = isSummaryCollapsed
      ? []
      : Object.entries(data.available_summaries).map(
          ([name, unit]: [string, UnitType | null]) => [
            name,
            unit === "yield" || name.toLowerCase().includes("yield")
              ? "weightedYield"
              : "sum",
          ]
        );

    const mixEntries: [string, string][] =
      !isCostColumnsCollapsed && selectedSummaryName
        ? data.available_mix_sources.map((source: string): [string, string] => [
            source,
            "sum",
          ])
        : [];

    return Object.fromEntries([
      ...baseEntries,
      ...summaryEntries,
      ...mixEntries,
    ]);
  }, [data, isCostColumnsCollapsed, selectedSummaryName, isSummaryCollapsed]);

  // Hide aggregation function labels in column headers (e.g., "sum")
  const aggregationFunctions = useMemo(() => {
    const yieldPercent =
      data === undefined
        ? null
        : weightedYieldPercent(
            filteredSeries,
            outputMassSummaryName(data.heats_summary)
          );

    return {
      ...GRID_AGGREGATION_FUNCTIONS_NO_LABEL,
      weightedYield: {
        apply: () => yieldPercent,
        label: "",
      },
    };
  }, [data, filteredSeries]);

  return { aggregationModel, aggregationFunctions };
};
